import React, { useState, useEffect, startTransition, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { useQuotes } from '../context/QuotesContext';
import { Save, Check, Trash2, Pencil, ChevronDown, FolderPlus, FolderMinus, FolderInput } from 'lucide-react';
import SummaryConfigThumbnail from './SummaryConfigThumbnail';
import { getColorById } from '../data/mockData';
import { useTranslation } from '../context/TranslationContext';
import { getColorByRAL, isStandardInteriorWhite } from '../data/ralColors';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  BTW_MULTIPLIER_INCL,
  BTW_RATE,
  calculateConfiguratorPrice,
  calcKozijnenVentilatorTotalInclVat,
  calcDoorVentilatorTotalInclVat,
  calcDoorSidePanelPartsPremium,
  countDoorVentilators,
  calcKozijnenOverheadTotalInclVat,
  calcKozijnenUnderlightTotalInclVat,
  calcKozijnenVensterbankTotalInclVat,
  calcKozijnenOpeningMechanismTotalInclVat,
  calcKozijnenOverheadOpeningMechanismTotalInclVat,
  calcKozijnenHorTotalInclVat,
  calcKozijnenHvlTotalInclVat,
  calcKozijnenRampDorpelExVat,
  calcKozijnenGlazing36mmExVat,
  calcKozijnenTexturedGlassExVat,
  calcDakkapelTripleGlazingPremiumInclVat,
  calcDakkapelSizePremiumInclVat,
  DAKKAPEL_OPTION_PRICES_INCL_VAT,
  normalizeDoorCatalogPriceToExVat,
  calcElectricScreenPriceExVat,
  calcElectricRollerShutterPriceExVat,
  calcVoordeurWoodJointPremium,
  countKozijnenOpeningMechanismParts,
  countKozijnenOverheadOpeningMechanismParts,
  countKozijnenOverheadParts,
  countKozijnenUnderlightParts,
  getSchuifpuiActiveVentilatorKeys,
  getPlastisolMeters,
} from '../lib/pdfPricing';
import { countTerrasdeurSandwichParapetSections } from '../lib/voordeurConfigHelpers';
import { getFilledAdditionalItems, sumAdditionalItemsPrice } from '../lib/additionalItems';
import { buildConfiguratorNavigationState } from '../lib/contactConfigurator';
import { captureDrawingSvg, svgHtmlToDataUrl } from '../lib/svgCapture';
import { buildKeralitSavedSvgAsync, getKeralitProfileLogo, buildPlastisolSavedSvgAsync, getPlastisolProfileLogo } from '../lib/keralitSvg';
import { buildDakkapelSavedSvgAsync } from '../lib/dakkapelSvg';
import {
  getDakkapelMaterialLabelKey,
  DAKKAPEL_SUN_PROTECTION,
  DAKKAPEL_ROOF_STRUCTURES,
  DAKKAPEL_ROOF_CONNECTIONS,
  isDakkapelOpeningElementType,
} from '../data/dakkapelOptions';
import { applyDeductionMargin, applyVekaProfileDiscount, calcVekaProfileDiscountAmount, applyDrawingPriceAdjustment, getDrawingPriceAdjustmentExVat } from '../lib/pricingAdjustments';
import { getRouteForDrawing, canOverwriteBackendQuote, computeStoredQuoteTotalInclVat, resolveQuoteContactId, withQuoteCreator, preserveCreatorFromBackendQuote, serializeQuoteDrawingsPayload } from '../lib/quoteSession';
import { computeDrawingsStoredTotalExVat, computeDrawingsStoredTotalInclVat, countQuoteDrawings, sumDrawingQuantities } from '../lib/quoteSections';
import { detectPageType } from '../lib/vmkQuoteTable';

const resolvePageType = (pageType, cfg) =>
  pageType || detectPageType(cfg);

const getLetterLabel = (index) => String.fromCharCode(65 + index);

const getConfiguratorTypeLabel = (type, t) => {
  if (!type) return '';
  const key = `configurator.${type}`;
  const translated = t(key);
  return translated !== key ? translated : type;
};

const getConfigSubtitle = (cfg, t) => {
  if (cfg?.productType === 'dakkapel') {
    const n = Array.isArray(cfg.elements) ? cfg.elements.length : 0;
    return n > 0 ? t('dakkapel.previewHint', { width: Math.round((cfg.width || 0) / 10), height: Math.round((cfg.height || 0) / 10), count: n }) : t('configurator.dakkapel');
  }
  const parts = cfg.parts || 1;
  if (parts > 1) {
    return `${parts} ${t('summary.partsLabel')}`;
  }
  if (cfg.woodJoint === 'joint-90') {
    return t('options.woodJoint90');
  }
  return t('summary.defaultProfile');
};

const formatDrawingSizeLabel = (quoteType, cfg) => {
  if (quoteType === 'plastisol' || cfg?.productType === 'plastisol') {
    const meters = getPlastisolMeters(cfg);
    if (meters > 0) {
      return `${Number(meters.toFixed(2))} m`;
    }
    return '—';
  }
  if (quoteType === 'dakkapel' || cfg?.productType === 'dakkapel') {
    return `${Math.round(cfg?.width || 0)} × ${Math.round(cfg?.height || 0)} mm`;
  }
  return `${cfg?.width ?? '—'} × ${cfg?.height ?? '—'}`;
};

const getListItemSubtitle = (quoteType, cfg, t) => {
  const typeLabel = getConfiguratorTypeLabel(quoteType, t);
  const configSubtitle = getConfigSubtitle(cfg, t);
  return typeLabel ? `${typeLabel} · ${configSubtitle}` : configSubtitle;
};

const Summary = ({ config, pricing, profileSystems, glassTextures, glazingTypes, colors, raamtypes, hardware, accessories, woodJointOptions, partsOptions, overheadOptions, divisionTypes, drawingBoardRef, pageType, compact = false, onConfigChange, onQuantityInputChange, saveCurrentRef, onSaved }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const editQuoteIdFromRoute = location.state?.editQuoteId || null;
  const [editingQuoteId, setEditingQuoteId] = useState(editQuoteIdFromRoute);
  const [saveNotice, setSaveNotice] = useState(null);
  
  // Helper function to translate glass texture and glazing type names
  const translateGlassTextureName = (id) => {
    const translations = {
      'helder-glas': t('options.clearGlass'),
      'clear': t('options.clearGlass'),
      'chinchilla': t('options.chinchilla'),
      'satinato': t('options.satinato'),
      'panel': t('options.panel')
    };
    return translations[id] || id;
  };
  
  const translateGlazingTypeName = (id) => {
    const translations = {
      'double': t('options.doubleGlass24'),
      'triple': t('options.doubleGlass36'),
      'double-24': t('options.doubleGlass24'),
      'double-36': t('options.doubleGlass36')
    };
    return translations[id] || id;
  };
  const {
    saveQuote,
    savedQuotes,
    removeQuote,
    clearAllQuotes,
    updateQuote,
    quoteSections,
    saveQuoteSection,
    ungroupQuoteSection,
    addCurrentDrawingsToQuoteSection,
    removeQuoteSection,
    includePricingInExport,
    setIncludePricingInExport,
    includeTemplateInExport,
    setIncludeTemplateInExport,
    includeNijBegunInExport,
    setIncludeNijBegunInExport,
    includeTotalPageInExport,
    setIncludeTotalPageInExport,
    activeBackendQuote,
    patchActiveBackendQuote,
  } = useQuotes();
  const { isAuthenticated, user } = useAuth();
  const [savedQuoteId, setSavedQuoteId] = useState(null);
  const [quantityInput, setQuantityInput] = useState(String(config.quantity ?? 1));
  const [pricingSettings, setPricingSettings] = useState(null);
  const [selectedKey, setSelectedKey] = useState(
    () => location.state?.selectedSavedQuoteId || 'current'
  );
  const [currentThumbnail, setCurrentThumbnail] = useState(null);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [sectionNameInput, setSectionNameInput] = useState('');
  const [openSectionIds, setOpenSectionIds] = useState(() => new Set());
  // Selection that was active before browsing/editing another saved drawing
  const selectionBeforeEditRef = useRef(
    location.state?.resumeAfterEdit?.selectedKey ||
      location.state?.selectedSavedQuoteId ||
      'current'
  );

  useEffect(() => {
    if (editQuoteIdFromRoute) {
      setEditingQuoteId(editQuoteIdFromRoute);
      // Editing loads into the live canvas — keep "current" selected while updating
      setSelectedKey('current');
    }
  }, [editQuoteIdFromRoute, location.key]);

  useEffect(() => {
    const selectedFromRoute = location.state?.selectedSavedQuoteId;
    if (selectedFromRoute) {
      setSelectedKey(selectedFromRoute);
    }
  }, [location.key, location.state?.selectedSavedQuoteId]);

  const rememberSelectionBeforeBrowse = useCallback((nextKey) => {
    if (editingQuoteId || editQuoteIdFromRoute) return;
    // Only remember the prior ACTIVE when actually switching cards
    if (nextKey !== selectedKey) {
      selectionBeforeEditRef.current = selectedKey;
    }
    setSelectedKey(nextKey);
  }, [editingQuoteId, editQuoteIdFromRoute, selectedKey]);

  /** Same path as "Tekening bewerken" — load the saved drawing onto the live board. */
  const startEditSavedDrawing = useCallback(
    (quote) => {
      if (!quote || editingQuoteId || editQuoteIdFromRoute) return;
      const route = getRouteForDrawing(quote);
      const resumeAfterEdit = {
        selectedKey: selectionBeforeEditRef.current || 'current',
        config: JSON.parse(JSON.stringify(config)),
        pathname: location.pathname,
      };
      navigate(route, {
        state: buildConfiguratorNavigationState(
          location.state,
          {
            loadQuoteConfig: quote.config,
            editQuoteId: quote.id,
            returnTo: location.pathname,
            resumeAfterEdit,
          },
          user?.id
        ),
      });
    },
    [
      config,
      editQuoteIdFromRoute,
      editingQuoteId,
      location.pathname,
      location.state,
      navigate,
      user?.id,
    ]
  );

  const clearEditSession = useCallback((options = {}) => {
    const selectQuoteId = options.selectQuoteId || null;
    const selectCurrent = options.selectCurrent === true;
    const keepConfig = options.keepConfig || null;
    setEditingQuoteId(null);
    if (selectCurrent) {
      setSelectedKey('current');
    } else if (selectQuoteId) {
      setSelectedKey(selectQuoteId);
    }
    const shouldReplaceRoute =
      editQuoteIdFromRoute ||
      location.state?.loadQuoteConfig ||
      location.state?.selectedSavedQuoteId ||
      location.state?.editQuoteId ||
      location.state?.resumeAfterEdit ||
      selectQuoteId ||
      selectCurrent ||
      keepConfig;
    if (shouldReplaceRoute) {
      navigate(location.pathname, {
        replace: true,
        state: buildConfiguratorNavigationState(
          location.state,
          {
            ...(selectCurrent
              ? {}
              : selectQuoteId
                ? { selectedSavedQuoteId: selectQuoteId }
                : {}),
            // Restore prior board (or keep updated drawing) after leaving edit mode
            ...(keepConfig ? { loadQuoteConfig: keepConfig } : {}),
          },
          user?.id
        ),
      });
    }
  }, [editQuoteIdFromRoute, location.pathname, location.state, navigate, user?.id]);

  const selectCurrentConfiguration = useCallback(() => {
    // Already on the live canvas — don't clear edit/load state (avoids remount → defaults)
    if (selectedKey === 'current') {
      return;
    }
    selectionBeforeEditRef.current = 'current';
    // Switching back from a saved drawing: keep the board config so Routes remount
    // (keyed by location.key) does not wipe the drawing to product defaults.
    clearEditSession({
      selectCurrent: true,
      keepConfig: location.state?.loadQuoteConfig || config,
    });
  }, [clearEditSession, config, location.state, selectedKey]);

  const resolvedPageType = resolvePageType(pageType, config);
  const projectQuotes = savedQuotes;
  const selectedQuote =
    selectedKey === 'current' ? null : projectQuotes.find((quote) => quote.id === selectedKey);
  const isCurrentSelection = !selectedQuote;
  
  // Sync local input state with config
  useEffect(() => {
    setQuantityInput(String(config.quantity ?? 1));
  }, [config.quantity]);

  useEffect(() => {
    if (selectedKey !== 'current' && !projectQuotes.some((quote) => quote.id === selectedKey)) {
      setSelectedKey('current');
    }
  }, [projectQuotes, selectedKey]);

  useEffect(() => {
    let cancelled = false;

    const refreshThumbnail = async () => {
      if (pageType === 'keralit') {
        const svg = await buildKeralitSavedSvgAsync(config);
        if (!cancelled) setCurrentThumbnail(svg);
        return;
      }
      if (pageType === 'plastisol') {
        const svg = await buildPlastisolSavedSvgAsync(config);
        if (!cancelled) setCurrentThumbnail(svg);
        return;
      }
      if (pageType === 'dakkapel') {
        const svg = await buildDakkapelSavedSvgAsync(config);
        if (!cancelled) setCurrentThumbnail(svg);
        return;
      }
      if (!cancelled) setCurrentThumbnail(captureDrawingSvg(drawingBoardRef));
    };

    const timer = setTimeout(() => {
      requestAnimationFrame(() => {
        refreshThumbnail();
      });
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [config, drawingBoardRef, pageType]);

  // Fetch pricing settings if user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      axios.get(`${API_URL}/api/pricing-settings`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      })
      .then(response => {
        setPricingSettings(response.data);
      })
      .catch(() => {
        // Pricing settings might not exist yet, that's okay
        setPricingSettings(null);
      });
    }
  }, [isAuthenticated]);
  
  // Get quantity from config, default to 1 (for display use current input when valid so total updates as user types)
  const quantityFromInput = parseInt(quantityInput, 10);
  const quantity = (!isNaN(quantityFromInput) && quantityFromInput >= 1) ? quantityFromInput : (config.quantity ?? 1);
  // Calculate area in square meters
  const areaSqm = (config.width * config.height) / 1000000; // Convert mm² to m²
  
  // PDF-derived pricing (VMK quotation 20260609110503987testkozijnenprijsv1.pdf)
  const { price: basePrice, hasPricing } = calculateConfiguratorPrice(config, pageType);
  const additionalItemsTotal = sumAdditionalItemsPrice(config.additionalItems);

  // Calculate option prices (all items are included, no individual prices shown)
  const calculateOptionPrices = () => {
    const items = [];
    let totalOptions = 0;
    const isVoordeurConfig =
      pageType === 'voordeur' || config.doorStyle !== undefined;
    const isAchterdeurConfig = pageType === 'achterdeur';
    const usesDoorRecreationPricing = isVoordeurConfig || isAchterdeurConfig;

    // Check if this is Kozijnen (windows) - base price already includes all default options
    const isKozijnenPart1 = (pageType === 'kozijnen' || pageType === undefined) && 
                            config.parts === 1 && 
                            !config.slidingPosition;
    
    // Check if this is Kozijnen (windows) with 2+ parts - base price already includes all options
    const isKozijnenMultiParts = (pageType === 'kozijnen' || pageType === undefined) && 
                                 config.parts >= 2 && 
                                 config.parts <= 5 &&
                                 !config.slidingPosition;
    // Terrasdeur / Achterdeur base price already includes default options (profile, glass, glazing, colors, etc.)
    const isTerrasdeur = pageType === 'terrasdeur';
    const isAchterdeur = pageType === 'achterdeur';
    const isKeralit = pageType === 'keralit';
    const isPlastisol = pageType === 'plastisol';
    const isDakkapel = pageType === 'dakkapel';
    const isAreaCladding = isKeralit || isPlastisol;
    
    // Profile System - show if profileSystems array has items and config has profileSystem
    if (!usesDoorRecreationPricing && profileSystems && profileSystems.length > 0 && config.profileSystem) {
      const profileSystem = profileSystems.find(p => p.id === config.profileSystem);
      const profilePrice = pricing.profileSystem?.[config.profileSystem] || 0;
      items.push({ name: `${t('options.profile')}: ${profileSystem?.name || config.profileSystem}`, price: 0, included: true });
      if (!isKozijnenPart1 && !isKozijnenMultiParts && !isTerrasdeur && !isAchterdeur) {
        totalOptions += profilePrice;
      }
    }
    
    // Glass Texture - Achterdeur and Kozijnen use same prices from pricing.kozijnen (Kozijnen.jsx options)
    if (!usesDoorRecreationPricing && config.glassTexture) {
      const glassTexture = glassTextures.find(g => g.id === config.glassTexture);
      const glassPrice = (isKozijnenPart1 || isKozijnenMultiParts || isAchterdeur)
        ? (pricing.kozijnen?.glassTexture?.[config.glassTexture] ?? pricing.glassTexture?.[config.glassTexture] ?? 0)
        : (pricing.glassTexture?.[config.glassTexture] || 0);
      items.push({ name: `${t('options.glass')}: ${translateGlassTextureName(config.glassTexture)}`, price: glassPrice, included: true });
      if (!isKozijnenPart1 && !isKozijnenMultiParts && !isTerrasdeur && !isAchterdeur) {
        totalOptions += glassPrice;
      }
      if (isTerrasdeur && glassPrice) {
        totalOptions += glassPrice;
      }
      if (isAchterdeur && glassPrice) {
        totalOptions += glassPrice;
      }
    }
    
    // Glazing Type - Achterdeur and Kozijnen use same prices from pricing.kozijnen (Kozijnen.jsx options)
    if (!usesDoorRecreationPricing && config.glazingType) {
      const glazingType = glazingTypes.find(g => g.id === config.glazingType);
      const glazingPrice = (isKozijnenPart1 || isKozijnenMultiParts || isAchterdeur)
        ? (pricing.kozijnen?.glazingType?.[config.glazingType] ?? pricing.glazingType?.[config.glazingType] ?? 0)
        : (pricing.glazingType?.[config.glazingType] || 0);
      items.push({ name: `${t('options.glazing')}: ${translateGlazingTypeName(config.glazingType)}`, price: glazingPrice, included: true });
      if (!isKozijnenPart1 && !isKozijnenMultiParts && !isTerrasdeur && !isAchterdeur) {
        totalOptions += glazingPrice;
      }
      if (isTerrasdeur && (config.glazingType === 'double-36' || config.glazingType === 'triple') && glazingPrice) {
        totalOptions += glazingPrice;
      }
      if (isAchterdeur && glazingPrice) {
        totalOptions += glazingPrice;
      }
    }
    
    // External Frame Color - always show (included)
    if (!usesDoorRecreationPricing && (config.exteriorColor || config.color)) {
      const externalColorId = config.exteriorColor || config.color;
      const colorData = getColorById(externalColorId);
      const colorName = colorData?.name || (colorData?.ral ? `RAL ${colorData.ral}` : externalColorId);
      const colorPrice = externalColorId === 'anthracite' ? 0 : (pricing.color?.[externalColorId] || 0);
      items.push({ name: `${t('options.externalColor')}: ${colorName}`, price: 0, included: true });
      if (!isKozijnenPart1 && !isKozijnenMultiParts && !isTerrasdeur && !isAchterdeur) {
        totalOptions += colorPrice;
      }
    }
    
    // Interior Frame Color - handle both config.interiorFrameColor (for windows) and config.interiorColor (for doors like Schuifpui)
    const hasInteriorFrames = config.partWindowTypes?.some(type => 
      ['fixed-glass', 'tilt-only', 'tilt-turn-left', 'tilt-turn-right'].includes(type)
    ) || config.overheadPartWindowTypes?.some(type => 
      ['fixed-glass', 'tilt-only', 'tilt-turn-left', 'tilt-turn-right'].includes(type)
    ) || config.underlightPartWindowTypes?.some(type =>
      ['fixed-glass', 'tilt-only', 'tilt-turn-left', 'tilt-turn-right'].includes(type)
    );
    
    // Kozijnen: show binnenvlak in summary; openable sashes use draaideel on drawing
    if (config.draaideelColor && !usesDoorRecreationPricing) {
      const draaideelColorData = getColorById(config.draaideelColor);
      const draaideelColorName =
        draaideelColorData?.name ||
        (draaideelColorData?.ral ? `RAL ${draaideelColorData.ral}` : config.draaideelColor);
      items.push({ name: `${t('options.draaideelColor')}: ${draaideelColorName}`, price: 0, included: true });
    }

    if (config.interiorFrameColor) {
      const interiorColorData = getColorById(config.interiorFrameColor);
      const interiorColorName = interiorColorData?.name || (interiorColorData?.ral ? `RAL ${interiorColorData.ral}` : config.interiorFrameColor);
      const interiorColorPrice = isStandardInteriorWhite(config.interiorFrameColor) ? 0 : (pricing.interiorFrameColor[config.interiorFrameColor] || 0);
      items.push({ name: `${t('options.interiorColor')}: ${interiorColorName}`, price: 0, included: true });
      if (hasInteriorFrames && !isKozijnenPart1 && !isKozijnenMultiParts) {
        totalOptions += interiorColorPrice;
      }
    }
    
    // For sliding doors (Schuifpui), always show interiorColor if it exists
    // Other door types (Terrasdeur, Achterdeur, Voordeur) handle colors in their specific sections
    if (config.interiorColor && (pageType === 'schuifpui' || config.doorType === 'sliding')) {
      const interiorColorData = getColorById(config.interiorColor);
      const interiorColorName = interiorColorData?.name || (interiorColorData?.ral ? `RAL ${interiorColorData.ral}` : config.interiorColor);
      const interiorColorPrice = isStandardInteriorWhite(config.interiorColor) ? 0 : (pricing.interiorFrameColor?.[config.interiorColor] || 0);
      items.push({ name: `${t('options.interiorColor')}: ${interiorColorName}`, price: 0, included: true });
      totalOptions += interiorColorPrice;
    }
    
    // Sliding Door (Schuifpui) specific options
    if (pageType === 'schuifpui' || config.doorType === 'sliding') {
      // Sliding Position (included)
      if (config.slidingPosition) {
        const slidingPositionNames = {
          'left': t('options.slidingLeft'),
          'right': t('options.slidingRight'),
          'center': t('options.slidingCenter')
        };
        items.push({ name: `${t('options.slidingPosition')}: ${slidingPositionNames[config.slidingPosition] || config.slidingPosition}`, price: 0, included: true });
      }
      
      // Threshold Height (included)
      if (config.thresholdHeight) {
        const thresholdPrice = pricing.schuifpui?.thresholdHeight?.[config.thresholdHeight] || 0;
        items.push({ name: `${t('options.threshold')}: ${config.thresholdHeight}`, price: 0, included: true });
        totalOptions += thresholdPrice;
      }
      
      // Right Glazing Type (included)
      if (config.rightGlazingType) {
        const rightGlazingType = glazingTypes.find(g => g.id === config.rightGlazingType);
        const rightGlazingPrice = pricing.glazingType?.[config.rightGlazingType] || 0;
        items.push({ name: `${t('options.glazing')} (${t('options.right')}): ${translateGlazingTypeName(config.rightGlazingType)}`, price: 0, included: true });
        totalOptions += rightGlazingPrice;
      }

      // Ventilator
      const ventilatorKeys = getSchuifpuiActiveVentilatorKeys(config);
      if (ventilatorKeys.length > 0) {
        const ventilatorLabelByKey = {
          left: t('options.ventilatorLeftFrame'),
          right: t('options.ventilatorRightFrame'),
          centerLeft: t('options.ventilatorLeftFrame'),
          centerRight: t('options.ventilatorRightFrame'),
          centerCenter: t('options.ventilatorCenterFrame'),
          centerThird: t('options.ventilatorThirdFrame'),
        };
        ventilatorKeys.forEach((key) => {
          items.push({
            name: ventilatorLabelByKey[key] || t('options.withVentilator'),
            price: 0,
            included: true,
          });
        });
      }
    }
    
    // Back Door (Achterdeur) legacy option list — display only; pricing uses door recreation model
    if (
      pageType !== 'achterdeur' &&
      pageType !== 'voordeur' &&
      (config.topLight !== undefined || config.numberOfParts !== undefined)
    ) {
      const isAchterdeur = pageType === 'achterdeur';
      // Top Light (included in base for Achterdeur)
      const topLightPrice = pricing.achterdeur?.topLight?.[config.topLight] || 0;
      if (topLightPrice > 0 || config.topLight) {
        const topLightNames = {
          'no-top-light': t('options.noTopLight'),
          'with-top-light': t('options.withTopLight')
        };
        items.push({ name: `${t('options.topLight')}: ${topLightNames[config.topLight] || config.topLight}`, price: 0, included: true });
        if (!isAchterdeur) totalOptions += topLightPrice;
      }
      
      // Number of Parts (included in base for Achterdeur)
      const numberOfPartsPrice = pricing.achterdeur?.numberOfParts?.[config.numberOfParts] || 0;
      if (numberOfPartsPrice > 0 || config.numberOfParts) {
        const numberOfPartsNames = {
          'one-part': t('options.onePart'),
          'two-parts': t('options.twoParts')
        };
        items.push({ name: `${t('options.parts')}: ${numberOfPartsNames[config.numberOfParts] || config.numberOfParts}`, price: 0, included: true });
        if (!isAchterdeur) totalOptions += numberOfPartsPrice;
      }
      
      // Panel Type (only when two-parts is selected) – included in base for Achterdeur
      if (config.numberOfParts === 'two-parts' && config.panelType) {
        const panelTypePrice = pricing.achterdeur?.panelType?.[config.panelType] || 0;
        const panelTypeNames = {
          'sandwich-no-mullions': t('options.sandwichNoMullions'),
          'glass': t('options.glass'),
          'horizontal-mullions': t('options.horizontalMullions'),
          'vertical-mullions': t('options.verticalMullions')
        };
        items.push({ name: `${t('options.panelType')}: ${panelTypeNames[config.panelType] || config.panelType}`, price: 0, included: true });
        if (!isAchterdeur) totalOptions += panelTypePrice;
      }
      
      // Threshold Height (included)
      const thresholdPrice = pricing.achterdeur?.thresholdHeight?.[config.thresholdHeight] || 0;
      if (thresholdPrice > 0 || config.thresholdHeight) {
        items.push({ name: `${t('options.threshold')}: ${config.thresholdHeight}`, price: 0, included: true });
        totalOptions += thresholdPrice;
      }
      
      // Parapet (when two-parts is selected) – included in base for Achterdeur
      const parapetPrice = pricing.achterdeur?.parapet?.[config.parapet] || 0;
      if (parapetPrice > 0 || config.parapet) {
        items.push({ name: `${t('options.parapet')}: ${config.parapet === 'with-parapet' ? t('options.withParapet') : t('options.noParapet')}`, price: 0, included: true });
        if (!isAchterdeur) totalOptions += parapetPrice;
      }
      
      // Security (included in base for Achterdeur – default 1.325,58 already includes SKG 2)
      items.push({ name: `${t('options.security')}: ${t('options.skg2Rating')}`, price: 0, included: true });
      const securityPrice = pricing.achterdeur?.security?.[config.security] || 0;
      if (!isAchterdeur) totalOptions += securityPrice;
      
      // Top Light Overhead Parts (when topLight is with-top-light)
      if (config.topLight === 'with-top-light' && config.overheadParts > 1) {
        const overheadPartsPrice = pricing.achterdeur?.overheadParts?.[config.overheadParts] || 0;
        if (overheadPartsPrice > 0) {
          items.push({ name: `${config.overheadParts} ${t('options.overheadParts')}`, price: overheadPartsPrice });
          totalOptions += overheadPartsPrice;
        }
        
        // Overhead window types (included)
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          const typePrice = pricing.raamtype[type] || 0;
          items.push({ name: `${t('options.overhead')} ${t('options.parts')} ${index + 1}: ${raamtype?.name || type}`, price: 0, included: true });
          totalOptions += typePrice;
        });
      }
      
      // Interior/Exterior Colors (for Achterdeur) (included) – base already includes default colors
      if (config.exteriorColor) {
        const exteriorColorData = getColorById(config.exteriorColor);
        const exteriorColorName = exteriorColorData?.name || (exteriorColorData?.ral ? `RAL ${exteriorColorData.ral}` : config.exteriorColor);
        const exteriorColorPrice = config.exteriorColor === 'anthracite' ? 0 : (pricing.color[config.exteriorColor] || 0);
        items.push({ name: `${t('options.exteriorColor')}: ${exteriorColorName}`, price: 0, included: true });
        if (!isAchterdeur) totalOptions += exteriorColorPrice;
      }

      if (config.draaideelColor) {
        const draaideelColorData = getColorById(config.draaideelColor);
        const draaideelColorName =
          draaideelColorData?.name ||
          (draaideelColorData?.ral ? `RAL ${draaideelColorData.ral}` : config.draaideelColor);
        items.push({ name: `${t('options.draaideelColor')}: ${draaideelColorName}`, price: 0, included: true });
      }

      if (config.interiorColor) {
        const interiorColorData = getColorById(config.interiorColor);
        const interiorColorName = interiorColorData?.name || (interiorColorData?.ral ? `RAL ${interiorColorData.ral}` : config.interiorColor);
        const interiorColorPrice = isStandardInteriorWhite(config.interiorColor) ? 0 : (pricing.interiorFrameColor[config.interiorColor] || 0);
        items.push({ name: `${t('options.interiorColor')}: ${interiorColorName}`, price: 0, included: true });
        if (!isAchterdeur) totalOptions += interiorColorPrice;
      }
    }
    else if (isAchterdeurConfig) {
      const includedOptions = [];
      if (config.profileSystem) {
        const profileSystem = profileSystems?.find((p) => p.id === config.profileSystem);
        includedOptions.push(`${t('options.profile')}: ${profileSystem?.name || config.profileSystem}`);
      }
      if (config.glassTexture) {
        includedOptions.push(`${t('options.glass')}: ${translateGlassTextureName(config.glassTexture)}`);
      }
      if (config.glazingType) {
        includedOptions.push(`${t('options.glazing')}: ${translateGlazingTypeName(config.glazingType)}`);
      }
      if (includedOptions.length > 0) {
        items.push({
          name: includedOptions.join(' · '),
          price: 0,
          included: true,
        });
      }

      if (config.thresholdHeight) {
        items.push({ name: `${t('options.threshold')}: ${config.thresholdHeight}`, price: 0, included: true });
      }

      if (config.numberOfParts === 'two-parts') {
        items.push({ name: `${t('options.parts')}: ${t('options.twoParts')}`, price: 0, included: true });
        if (config.panelType) {
          const panelTypeNames = {
            'sandwich-no-mullions': t('options.sandwichNoMullions'),
            glass: t('options.glass'),
            'horizontal-mullions': t('options.horizontalMullions'),
            'vertical-mullions': t('options.verticalMullions'),
          };
          items.push({
            name: `${t('options.panelType')}: ${panelTypeNames[config.panelType] || config.panelType}`,
            price: 0,
            included: true,
          });
        }
      } else if (config.numberOfParts === 'one-part') {
        items.push({ name: `${t('options.parts')}: ${t('options.onePart')}`, price: 0, included: true });
      }

      items.push({ name: `${t('options.security')}: ${t('options.skg2Rating')}`, price: 0, included: true });

      // Colors: Buitenvlakkleur → Kleur draaideel → Binnenvlakkleur
      if (config.exteriorColor) {
        const exteriorColorData = getColorById(config.exteriorColor);
        const exteriorColorName = exteriorColorData?.name || (exteriorColorData?.ral ? `RAL ${exteriorColorData.ral}` : config.exteriorColor);
        items.push({ name: `${t('options.exteriorColor')}: ${exteriorColorName}`, price: 0, included: true });
      }

      if (config.draaideelColor) {
        const draaideelColorData = getColorById(config.draaideelColor);
        const draaideelColorName =
          draaideelColorData?.name ||
          (draaideelColorData?.ral ? `RAL ${draaideelColorData.ral}` : config.draaideelColor);
        items.push({ name: `${t('options.draaideelColor')}: ${draaideelColorName}`, price: 0, included: true });
      }

      if (config.interiorColor) {
        const interiorColorData = getColorById(config.interiorColor);
        const interiorColorName = interiorColorData?.name || (interiorColorData?.ral ? `RAL ${interiorColorData.ral}` : config.interiorColor);
        items.push({ name: `${t('options.interiorColor')}: ${interiorColorName}`, price: 0, included: true });
      }
    }
    // Front Door (Voordeur) specific options (display-only; PDF lines above)
    else if (isVoordeurConfig) {
      // PDF-priced options are listed above; these are included in the PDF unit price
      const includedOptions = [];
      if (config.profileSystem) {
        const profileSystem = profileSystems?.find((p) => p.id === config.profileSystem);
        includedOptions.push(`${t('options.profile')}: ${profileSystem?.name || config.profileSystem}`);
      }
      if (config.glassTexture) {
        includedOptions.push(`${t('options.glass')}: ${translateGlassTextureName(config.glassTexture)}`);
      }
      if (config.glazingType) {
        // Glazing type has no price effect on voordeur for now.
        includedOptions.push(`${t('options.glazing')}: ${translateGlazingTypeName(config.glazingType)}`);
      }
      if (includedOptions.length > 0) {
        items.push({
          name: includedOptions.join(' · '),
          price: 0,
          included: true,
        });
      }

      // Parapet/Handle Type (display; priced via breakdown on style-1 + 4 parts)
      if (config.parapet) {
        const parapetNames = {
          'standard-knob': t('options.standardDoorKnob'),
          'rvs-bar': t('options.rvsBar'),
        };
        const knobPriced =
          config.doorStyle === 'style-1' &&
          Number(config.numberOfParts) === 4 &&
          config.parapet === 'standard-knob';
        if (!knobPriced) {
          items.push({
            name: `${t('options.handle')}: ${parapetNames[config.parapet] || config.parapet}`,
            price: 0,
            included: true,
          });
        }
      }

      // Threshold Height (included)
      if (config.thresholdHeight) {
        items.push({ name: `${t('options.threshold')}: ${config.thresholdHeight}`, price: 0, included: true });
      }

      // Security (included)
      items.push({ name: `${t('options.security')}: ${t('options.skg2Rating')}`, price: 0, included: true });

      // Colors: Buitenvlakkleur → Kleur draaideel → Binnenvlakkleur
      if (config.exteriorColor) {
        const exteriorColorData = getColorById(config.exteriorColor);
        const exteriorColorName = exteriorColorData?.name || (exteriorColorData?.ral ? `RAL ${exteriorColorData.ral}` : config.exteriorColor);
        items.push({ name: `${t('options.exteriorColor')}: ${exteriorColorName}`, price: 0, included: true });
      }

      if (config.draaideelColor) {
        const draaideelColorData = getColorById(config.draaideelColor);
        const draaideelColorName =
          draaideelColorData?.name ||
          (draaideelColorData?.ral ? `RAL ${draaideelColorData.ral}` : config.draaideelColor);
        items.push({ name: `${t('options.draaideelColor')}: ${draaideelColorName}`, price: 0, included: true });
      }

      if (config.interiorColor) {
        const interiorColorData = getColorById(config.interiorColor);
        const interiorColorName = interiorColorData?.name || (interiorColorData?.ral ? `RAL ${interiorColorData.ral}` : config.interiorColor);
        items.push({ name: `${t('options.interiorColor')}: ${interiorColorName}`, price: 0, included: true });
      }
    }
    // Keralit / Plastisol cladding — priced via calculateConfiguratorPrice (m² or m)
    else if (isAreaCladding) {
      if (config.profileSystem) {
        const profileSystem = profileSystems?.find((p) => p.id === config.profileSystem);
        items.push({
          name: `${t('options.profile')}: ${profileSystem?.name || config.profileSystem}`,
          price: 0,
          included: true,
        });
      }

      if (isKeralit && config.lineType) {
        const lineTypeName =
          config.lineType === 'vertical'
            ? t('options.verticalLines')
            : t('options.horizontalLines');
        items.push({ name: `${t('options.lineType')}: ${lineTypeName}`, price: 0, included: true });
      }

      if (isKeralit && config.lineSpacing) {
        const spacingLabel =
          config.lineSpacing === '14cm'
            ? t('options.lineSpacing14cm')
            : t('options.lineSpacing19cm');
        items.push({ name: `${t('options.lineSpacing')}: ${spacingLabel}`, price: 0, included: true });
      }

      if (isKeralit) {
        const colorId = config.exteriorColor || config.interiorColor;
        if (colorId) {
          const colorData = getColorById(colorId);
          const colorName =
            colorData?.name ||
            (colorData?.ral ? `RAL ${colorData.ral}` : colorId);
          items.push({
            name: `${t('options.keralitFrameColor')}: ${colorName}`,
            price: 0,
            included: true,
          });
        }
      } else if (config.exteriorColor) {
        const exteriorColorData = getColorById(config.exteriorColor);
        const exteriorColorName =
          exteriorColorData?.name ||
          (exteriorColorData?.ral ? `RAL ${exteriorColorData.ral}` : config.exteriorColor);
        items.push({ name: `${t('options.exteriorColor')}: ${exteriorColorName}`, price: 0, included: true });
      }
    } else if (isDakkapel) {
      const toExVat = (inclVat) => normalizeDoorCatalogPriceToExVat(inclVat);
      const sizePremiumIncl = calcDakkapelSizePremiumInclVat(config.width, config.height);
      items.push({
        name: `${t('dakkapel.dimensions')}: ${Math.round(config.width || 0)} × ${Math.round(config.height || 0)} mm`,
        price: toExVat(sizePremiumIncl),
        included: sizePremiumIncl === 0,
      });
      if (config.elements?.length) {
        const tiltTurnCount = config.elements.filter((el) =>
          isDakkapelOpeningElementType(el?.type),
        ).length;
        const tiltPremiumIncl =
          tiltTurnCount * (DAKKAPEL_OPTION_PRICES_INCL_VAT.tiltTurnElement || 0);
        items.push({
          name: `${t('dakkapel.elementCount')}: ${config.elements.length}`,
          price: toExVat(tiltPremiumIncl),
          included: tiltPremiumIncl === 0,
        });
      }
      if (config.glazingType) {
        const glazingPremiumIncl = calcDakkapelTripleGlazingPremiumInclVat(config);
        items.push({
          name: `${t('options.glazing')}: ${translateGlazingTypeName(config.glazingType)}`,
          price: toExVat(glazingPremiumIncl),
          included: glazingPremiumIncl === 0,
        });
      }
      if (config.material) {
        const labelKey = getDakkapelMaterialLabelKey(config.material);
        const materialPremiumIncl =
          DAKKAPEL_OPTION_PRICES_INCL_VAT.materialPremium[config.material] || 0;
        items.push({
          name: `${t('dakkapel.material')}: ${labelKey ? t(labelKey) : config.material}`,
          price: toExVat(materialPremiumIncl),
          included: materialPremiumIncl === 0,
        });
      }
      if (config.claddingColor) {
        const colorId = config.claddingColor === 'custom' ? config.claddingColorCustom : config.claddingColor;
        const colorData = getColorById(colorId);
        const colorName =
          colorData?.name ||
          (colorData?.ral ? `RAL ${colorData.ral}` : config.claddingColor);
        items.push({ name: `${t('dakkapel.claddingColor')}: ${colorName}`, price: 0, included: true });
      }
      if (config.frameColor) {
        const colorId = config.frameColor === 'custom' ? config.frameColorCustom : config.frameColor;
        const colorData = getColorById(colorId);
        const colorName =
          colorData?.name ||
          (colorData?.ral ? `RAL ${colorData.ral}` : config.frameColor);
        items.push({ name: `${t('dakkapel.frameColor')}: ${colorName}`, price: 0, included: true });
      }
      if (config.fasciaColor) {
        const colorId = config.fasciaColor === 'custom' ? config.fasciaColorCustom : config.fasciaColor;
        const colorData = getColorById(colorId);
        const colorName =
          colorData?.name ||
          (colorData?.ral ? `RAL ${colorData.ral}` : config.fasciaColor);
        items.push({ name: `${t('dakkapel.fasciaColor')}: ${colorName}`, price: 0, included: true });
      }
      if (config.tiltTurnColor) {
        const colorId = config.tiltTurnColor === 'custom' ? config.tiltTurnColorCustom : config.tiltTurnColor;
        const colorData = getColorById(colorId);
        const colorName =
          colorData?.name ||
          (colorData?.ral ? `RAL ${colorData.ral}` : config.tiltTurnColor);
        items.push({ name: `${t('dakkapel.tiltTurnColor')}: ${colorName}`, price: 0, included: true });
      }
      if (config.roofStructure) {
        const roof = DAKKAPEL_ROOF_STRUCTURES.find((m) => m.id === config.roofStructure);
        items.push({
          name: `${t('dakkapel.roofStructure')}: ${roof ? t(roof.labelKey) : config.roofStructure}`,
          price: 0,
          included: true,
        });
      }
      if (config.roofConnection) {
        const conn = DAKKAPEL_ROOF_CONNECTIONS.find((m) => m.id === config.roofConnection);
        items.push({
          name: `${t('dakkapel.roofConnection')}: ${conn ? t(conn.labelKey) : config.roofConnection}`,
          price: 0,
          included: true,
        });
      }
      if (config.sunProtection && config.sunProtection !== 'none') {
        const sun = DAKKAPEL_SUN_PROTECTION.find((m) => m.id === config.sunProtection);
        const sunPremiumIncl =
          config.sunProtection === 'screens'
            ? DAKKAPEL_OPTION_PRICES_INCL_VAT.screens
            : config.sunProtection === 'shutters'
              ? DAKKAPEL_OPTION_PRICES_INCL_VAT.shutters
              : 0;
        items.push({
          name: `${t('dakkapel.sunProtection')}: ${sun ? t(sun.labelKey) : config.sunProtection}`,
          price: toExVat(sunPremiumIncl),
          included: sunPremiumIncl === 0,
        });
      }
      const insectCount = Math.max(0, Number(config.insectScreens) || 0);
      if (insectCount > 0) {
        const insectPremiumIncl = insectCount * DAKKAPEL_OPTION_PRICES_INCL_VAT.insectScreen;
        items.push({
          name: `${t('dakkapel.insectScreens')}: ${t('dakkapel.yesCount', { n: insectCount })}`,
          price: toExVat(insectPremiumIncl),
          included: false,
        });
      }
      if (config.permitApplication) {
        items.push({
          name: `${t('dakkapel.permitApplication')}: ${t('dakkapel.yes')}`,
          price: toExVat(DAKKAPEL_OPTION_PRICES_INCL_VAT.permitApplication),
          included: false,
        });
      }
      if (config.debrisRemoval) {
        items.push({
          name: `${t('dakkapel.debrisRemoval')}: ${t('dakkapel.yes')}`,
          price: toExVat(DAKKAPEL_OPTION_PRICES_INCL_VAT.debrisRemoval),
          included: false,
        });
      }
      if (config.airco) {
        items.push({
          name: `${t('dakkapel.airco')}: ${t('dakkapel.yes')}`,
          price: toExVat(DAKKAPEL_OPTION_PRICES_INCL_VAT.airco),
          included: false,
        });
      }
      const grilleCount = Math.max(0, Number(config.ventilationGrilles) || 0);
      if (grilleCount > 0) {
        items.push({
          name: `${t('dakkapel.ventilationGrilles')}: ${t(
            grilleCount === 1 ? 'dakkapel.yesGrilleCount' : 'dakkapel.yesGrilleCountPlural',
            { n: grilleCount }
          )}`,
          price: toExVat(grilleCount * DAKKAPEL_OPTION_PRICES_INCL_VAT.ventilationGrille),
          included: false,
        });
      }
    }
    // Terrace Door specific options
    else if (config.designType !== undefined) {
      // Design Type (included) - for Terrasdeur base price already includes design type, don't add again
      const designTypePrice = pricing.terrasdeur?.designType?.[config.designType] || 0;
      if (designTypePrice > 0 || config.designType) {
        const designTypeNames = {
          'no-panel': t('options.noPanel'),
          'left-panel': t('options.leftPanel'),
          'right-panel': t('options.rightPanel'),
          'both-panels': t('options.bothPanels')
        };
        items.push({ name: `${t('options.design')}: ${designTypeNames[config.designType] || config.designType}`, price: 0, included: true });
        if (pageType !== 'terrasdeur') totalOptions += designTypePrice;
      }
      
      // Door Type (included) - for Terrasdeur base price already includes door type, don't add again
      const doorTypePrice = pricing.terrasdeur?.doorType?.[config.doorType] || 0;
      if (doorTypePrice > 0 || config.doorType) {
        const doorTypeNames = {
          'normal': t('options.normal'),
          'top-light': t('options.topLight')
        };
        items.push({ name: `${t('options.doorType')}: ${doorTypeNames[config.doorType] || config.doorType}`, price: 0, included: true });
        if (pageType !== 'terrasdeur') totalOptions += doorTypePrice;
      }

      // Main door panel type when parapet is enabled (included in PDF unit price)
      if (config.parapet === 'with-parapet' && config.panelType) {
        const panelTypeNames = {
          'sandwich-no-mullions': t('options.sandwichNoMullions'),
          glass: t('options.glass'),
          'horizontal-mullions': t('options.horizontalMullions'),
          'vertical-mullions': t('options.verticalMullions'),
        };
        items.push({
          name: `${t('options.panelType')}: ${panelTypeNames[config.panelType] || config.panelType}`,
          price: 0,
          included: true,
        });
      }
      
      // Handle Position (included)
      if (config.handlePosition) {
        const handlePositionNames = {
          'left': `${t('options.handle')} ${t('options.left')}`,
          'right': `${t('options.handle')} ${t('options.right')}`
        };
        items.push({ name: `${t('options.handlePosition')}: ${handlePositionNames[config.handlePosition] || config.handlePosition}`, price: 0, included: true });
      }
      
      // Threshold Height (included)
      const thresholdPrice = pricing.terrasdeur?.thresholdHeight?.[config.thresholdHeight] || 0;
      if (thresholdPrice > 0 || config.thresholdHeight) {
        items.push({ name: `${t('options.threshold')}: ${config.thresholdHeight}`, price: 0, included: true });
        totalOptions += thresholdPrice;
      }
      
      // Parapet (included) – main door parapet
      const parapetPrice = pricing.terrasdeur?.parapet?.[config.parapet] || 0;
      if (parapetPrice > 0 || config.parapet) {
        items.push({ name: `${t('options.parapet')}: ${config.parapet === 'with-parapet' ? t('options.withParapet') : t('options.noParapet')}`, price: 0, included: true });
        totalOptions += parapetPrice;
      }
      // Side panel parapet (left/right panel settings) – per panel when Parapet Yes
      const sidePanelParapetPrice = pricing.terrasdeur?.sidePanelParapet ?? 0;
      const hasLeftPanelParapet = config.designType === 'left-panel' || config.designType === 'both-panels';
      const hasRightPanelParapet = config.designType === 'right-panel' || config.designType === 'both-panels';
      if (hasLeftPanelParapet && config.leftPanelParapet && sidePanelParapetPrice) {
        items.push({ name: `${t('options.leftPanel')} ${t('options.parapet')}: ${t('options.yes')}`, price: 0, included: true });
        totalOptions += sidePanelParapetPrice;
      }
      if (hasRightPanelParapet && config.rightPanelParapet && sidePanelParapetPrice) {
        items.push({ name: `${t('options.rightPanel')} ${t('options.parapet')}: ${t('options.yes')}`, price: 0, included: true });
        totalOptions += sidePanelParapetPrice;
      }

      const sidePanelOverheadPrice = pricing.terrasdeur?.sidePanelOverhead ?? 0;
      const hasLeftPanelOverhead = config.designType === 'left-panel' || config.designType === 'both-panels';
      const hasRightPanelOverhead = config.designType === 'right-panel' || config.designType === 'both-panels';
      if (hasLeftPanelOverhead && config.leftPanelOverhead && sidePanelOverheadPrice) {
        items.push({ name: `${t('options.leftPanel')} ${t('drawing.overhead')}: ${t('options.yes')}`, price: 0, included: true });
      }
      if (hasRightPanelOverhead && config.rightPanelOverhead && sidePanelOverheadPrice) {
        items.push({ name: `${t('options.rightPanel')} ${t('drawing.overhead')}: ${t('options.yes')}`, price: 0, included: true });
      }

      if (pageType === 'terrasdeur') {
        const sandwichSections = countTerrasdeurSandwichParapetSections(config);
        if (sandwichSections > 0) {
          items.push({
            name: `${t('options.sandwichNoMullions')} (${sandwichSections}×)`,
            price: 0,
            included: true,
          });
        }
      }
      
      // Lintels (included)
      if (config.lintels !== undefined) {
        items.push({ name: `${t('options.lintels')}: ${config.lintels ? t('options.yes') : t('options.no')}`, price: 0, included: true });
      }
      
      // Security Bracket (included) - always show as SKG 2 Rating
      items.push({ name: `${t('options.security')}: ${t('options.skg2Rating')}`, price: 0, included: true });
      const securityPrice = pricing.terrasdeur?.securityBracket?.[config.securityBracket] || 0;
      totalOptions += securityPrice;
      
      // Top Light Overhead Parts (when doorType is top-light) (included)
      if (config.doorType === 'top-light' && config.overheadParts > 1) {
        const overheadPartsPrice = pricing.terrasdeur?.overheadParts?.[config.overheadParts] || 0;
        if (overheadPartsPrice > 0 || config.overheadParts) {
          items.push({ name: `${config.overheadParts} Overhead Parts`, price: 0, included: true });
          totalOptions += overheadPartsPrice;
        }
        
        // Overhead window types (included)
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          const typePrice = pricing.raamtype[type] || 0;
          items.push({ name: `${t('options.overhead')} ${t('options.parts')} ${index + 1}: ${raamtype?.name || type}`, price: 0, included: true });
          totalOptions += typePrice;
        });
      }
      
      // Interior/Exterior Colors (for Terrasdeur) (included) - base already includes default colors, don't add again
      if (config.interiorColor) {
        const interiorColorData = getColorById(config.interiorColor);
        const interiorColorName = interiorColorData?.name || (interiorColorData?.ral ? `RAL ${interiorColorData.ral}` : config.interiorColor);
        const interiorColorPrice = isStandardInteriorWhite(config.interiorColor) ? 0 : (pricing.interiorFrameColor?.[config.interiorColor] || 0);
        items.push({ name: `${t('options.interiorColor')}: ${interiorColorName}`, price: 0, included: true });
        if (pageType !== 'terrasdeur') totalOptions += interiorColorPrice;
      }
      
      if (config.exteriorColor) {
        const exteriorColorData = getColorById(config.exteriorColor);
        const exteriorColorName = exteriorColorData?.name || (exteriorColorData?.ral ? `RAL ${exteriorColorData.ral}` : config.exteriorColor);
        const exteriorColorPrice = config.exteriorColor === 'anthracite' ? 0 : (pricing.color?.[config.exteriorColor] || 0);
        items.push({ name: `${t('options.exteriorColor')}: ${exteriorColorName}`, price: 0, included: true });
        if (pageType !== 'terrasdeur') totalOptions += exteriorColorPrice;
      }
    } else {
      // Window configurator logic
      // Parts multiplier is applied to base price, not as a separate line item
      
      // Division Type - always show (included) if config has divisionType
      if (config.divisionType && divisionTypes && divisionTypes.length > 0) {
        const divisionType = divisionTypes.find(d => d.id === config.divisionType);
        items.push({ name: `${t('options.division')}: ${divisionType?.name || config.divisionType}`, price: 0, included: true });
      }
      
      // Window types per part - always show (included)
      config.partWindowTypes?.forEach((type, index) => {
        const raamtype = raamtypes.find(r => r.id === type);
        const typePrice = pricing.raamtype[type] || 0;
        
        // For fixed-panel, append color and lines info to the name
        if (type === 'fixed-panel') {
          let panelInfo = [];
          
          // Add panel color - always show something (default to 'Anthracite Grey' if not set)
          const panelColorId = config.panelColors?.[index];
          if (panelColorId) {
            const panelColorData = getColorById(panelColorId);
            const panelColorName = panelColorData?.name || (panelColorData?.ral ? `RAL ${panelColorData.ral}` : panelColorId);
            panelInfo.push(panelColorName);
          } else {
            // Default color if none selected
            panelInfo.push('Anthracite Grey (RAL 7016)');
          }
          
          // Add panel lines info - always show
          const panelLineType = config.panelLineTypes?.[index] || 'none';
          if (panelLineType === 'horizontal') {
            panelInfo.push('Horizontal Lines');
          } else if (panelLineType === 'vertical') {
            panelInfo.push('Vertical Lines');
          } else {
            panelInfo.push('Plain');
          }
          
          const panelInfoStr = panelInfo.length > 0 ? ` - ${panelInfo.join(' - ')}` : '';
          items.push({ name: `${t('options.parts')} ${index + 1}: ${raamtype?.name || type}${panelInfoStr}`, price: 0, included: true });
        } else {
          items.push({ name: `${t('options.parts')} ${index + 1}: ${raamtype?.name || type}`, price: 0, included: true });
        }
        // Don't add window type price for Kozijnen part 1 or multi-parts (base price already includes it)
        if (!isKozijnenPart1 && !isKozijnenMultiParts) {
          totalOptions += typePrice;
        }
      });
      
      // Opening mechanism (Kozijnen): €350 incl. BTW per main part with tilt-turn / tilt-only
      const openingMechanismPriceInclVat = calcKozijnenOpeningMechanismTotalInclVat(config);
      if ((isKozijnenPart1 || isKozijnenMultiParts) && openingMechanismPriceInclVat > 0) {
        const openingMechanismPartCount = countKozijnenOpeningMechanismParts(config);
        items.push({
          name: `${t('options.selectOpeningMechanism')}${openingMechanismPartCount > 1 ? ` (×${openingMechanismPartCount})` : ''}`,
          price: openingMechanismPriceInclVat,
          included: true,
        });
      }
      
      // Hor (Kozijnen): €60 incl. BTW per opening main part when Hor is selected
      if ((isKozijnenPart1 || isKozijnenMultiParts) && config.hor === true) {
        const horPriceInclVat = calcKozijnenHorTotalInclVat(config);
        if (horPriceInclVat > 0) {
          const horOpeningPartCount = countKozijnenOpeningMechanismParts(config);
          const horColorData = getColorByRAL(config.horColor || '7016');
          const horColorName = horColorData?.ral ? `RAL ${horColorData.ral.replace(/^0+/, '')}` : 'RAL 7016';
          items.push({
            name: `${t('quote.hor')} (${horColorName})${horOpeningPartCount > 1 ? ` (×${horOpeningPartCount})` : ''}`,
            price: horPriceInclVat,
            included: true,
          });
        }
      }
      
      // Overhead (included)
      if (config.overhead === 'overhead' || (Array.isArray(config.overheadPerPart) && config.overheadPerPart.some(Boolean))) {
        const overheadPrice = pricing.overhead.overhead;
        const kozijnenOverheadParts = countKozijnenOverheadParts(config);
        const kozijnenOverheadPriceInclVat = calcKozijnenOverheadTotalInclVat(config);
        const overheadDisplayPrice =
          isKozijnenPart1 || isKozijnenMultiParts ? kozijnenOverheadPriceInclVat : overheadPrice;
        const overheadLabel =
          isKozijnenPart1 || isKozijnenMultiParts
            ? `${t('options.overhead')}${kozijnenOverheadParts > 1 ? ` (×${kozijnenOverheadParts})` : ''}`
            : t('options.overhead');
        items.push({ name: overheadLabel, price: overheadDisplayPrice, included: true });
        // Don't add overhead price for Kozijnen (included in calculateConfiguratorPrice)
        if (!isKozijnenMultiParts && !isKozijnenPart1) {
          totalOptions += overheadPrice;
        }

        // Overhead opening mechanism (Kozijnen): €500 incl. BTW per bovenlicht draai/kiep
        const overheadOpeningPriceInclVat = calcKozijnenOverheadOpeningMechanismTotalInclVat(config);
        if ((isKozijnenPart1 || isKozijnenMultiParts) && overheadOpeningPriceInclVat > 0) {
          const overheadOpeningCount = countKozijnenOverheadOpeningMechanismParts(config);
          items.push({
            name: `${t('options.overhead')} — ${t('options.selectOpeningMechanism')}${overheadOpeningCount > 1 ? ` (×${overheadOpeningCount})` : ''}`,
            price: overheadOpeningPriceInclVat,
            included: true,
          });
        }
        
        // Overhead parts (included) — non-kozijnen legacy breakdown
        if (config.overheadParts > 1 && !isKozijnenPart1 && !isKozijnenMultiParts) {
          const overheadPartsPrice = (config.overheadParts - 1) * pricing.overheadPart;
          items.push({ name: `${config.overheadParts} Overhead Parts`, price: 0, included: true });
          // Don't add overhead parts price for Kozijnen multi-parts (base price already includes it)
          if (!isKozijnenMultiParts) {
            totalOptions += overheadPartsPrice;
          }
        }
        
        // Overhead window types - always show (included)
        config.overheadPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          const typePrice = pricing.raamtype[type] || 0;
          
          // For fixed-panel, append color and lines info to the name
          if (type === 'fixed-panel') {
            let panelInfo = [];
            
            // Add panel color - always show something (default to 'Anthracite Grey' if not set)
            const panelColorId = config.panelOverheadColors?.[index];
            if (panelColorId) {
              const panelColorData = getColorById(panelColorId);
              const panelColorName = panelColorData?.name || (panelColorData?.ral ? `RAL ${panelColorData.ral}` : panelColorId);
              panelInfo.push(panelColorName);
            } else {
              // Default color if none selected
              panelInfo.push('Anthracite Grey (RAL 7016)');
            }
            
            // Add panel lines info - always show
            const panelLineType = config.panelOverheadLineTypes?.[index] || 'none';
            if (panelLineType === 'horizontal') {
              panelInfo.push('Horizontal Lines');
            } else if (panelLineType === 'vertical') {
              panelInfo.push('Vertical Lines');
            } else {
              panelInfo.push('Plain');
            }
            
            const panelInfoStr = panelInfo.length > 0 ? ` - ${panelInfo.join(' - ')}` : '';
            items.push({ name: `${t('options.overhead')} ${t('options.parts')} ${index + 1}: ${raamtype?.name || type}${panelInfoStr}`, price: 0, included: true });
          } else {
            items.push({ name: `${t('options.overhead')} ${t('options.parts')} ${index + 1}: ${raamtype?.name || type}`, price: 0, included: true });
          }
          totalOptions += typePrice;
        });
      }
      
      // Underlight (included)
      if (config.underlight === 'underlight' || (Array.isArray(config.underlightPerPart) && config.underlightPerPart.some(Boolean))) {
        const underlightPrice = pricing.underlight?.underlight || 0;
        const kozijnenUnderlightParts = countKozijnenUnderlightParts(config);
        const kozijnenUnderlightPriceInclVat = calcKozijnenUnderlightTotalInclVat(config);
        const underlightDisplayPrice =
          isKozijnenPart1 || isKozijnenMultiParts ? kozijnenUnderlightPriceInclVat : underlightPrice;
        const underlightLabel =
          isKozijnenPart1 || isKozijnenMultiParts
            ? `${t('options.underlight')}${kozijnenUnderlightParts > 1 ? ` (×${kozijnenUnderlightParts})` : ''}`
            : t('options.underlight');
        items.push({ name: underlightLabel, price: underlightDisplayPrice, included: true });
        // Don't add underlight price for Kozijnen (included in calculateConfiguratorPrice)
        if (!isKozijnenPart1 && !isKozijnenMultiParts) {
          totalOptions += underlightPrice;
        }
        
        // Underlight parts (included) — non-kozijnen legacy breakdown
        if (config.underlightParts > 1 && !isKozijnenPart1 && !isKozijnenMultiParts) {
          const underlightPartsPrice = (config.underlightParts - 1) * (pricing.underlightPart || 0);
          if (underlightPartsPrice > 0) {
            items.push({ name: `${config.underlightParts} ${t('options.underlightParts')}`, price: 0, included: true });
            if (!isKozijnenPart1 && !isKozijnenMultiParts) {
              totalOptions += underlightPartsPrice;
            }
          }
        }
        
        // Underlight window types - always show (included)
        config.underlightPartWindowTypes?.forEach((type, index) => {
          const raamtype = raamtypes.find(r => r.id === type);
          const typePrice = pricing.raamtype[type] || 0;
          
          // For fixed-panel, append color and lines info to the name
          if (type === 'fixed-panel') {
            let panelInfo = [];
            
            // Add panel color - always show something (default to 'Anthracite Grey' if not set)
            const panelColorId = config.panelUnderlightColors?.[index];
            if (panelColorId) {
              const panelColorData = getColorById(panelColorId);
              const panelColorName = panelColorData?.name || (panelColorData?.ral ? `RAL ${panelColorData.ral}` : panelColorId);
              panelInfo.push(panelColorName);
            } else {
              // Default color if none selected
              panelInfo.push('Anthracite Grey (RAL 7016)');
            }
            
            // Add panel lines info - always show
            const panelLineType = config.panelUnderlightLineTypes?.[index] || 'none';
            if (panelLineType === 'horizontal') {
              panelInfo.push('Horizontal Lines');
            } else if (panelLineType === 'vertical') {
              panelInfo.push('Vertical Lines');
            } else {
              panelInfo.push('Plain');
            }
            
            const panelInfoStr = panelInfo.length > 0 ? ` - ${panelInfo.join(' - ')}` : '';
            items.push({ name: `${t('options.underlight')} ${t('options.parts')} ${index + 1}: ${raamtype?.name || type}${panelInfoStr}`, price: 0, included: true });
          } else {
            items.push({ name: `${t('options.underlight')} ${t('options.parts')} ${index + 1}: ${raamtype?.name || type}`, price: 0, included: true });
          }
          totalOptions += typePrice;
        });
      }
      
      // Vensterbank (Kozijnen): €75 incl. BTW when 30 mm window sill is selected
      const vensterbankPriceInclVat = calcKozijnenVensterbankTotalInclVat(config);
      if ((isKozijnenPart1 || isKozijnenMultiParts) && vensterbankPriceInclVat > 0) {
        items.push({
          name: t('options.vensterbankDesc'),
          price: vensterbankPriceInclVat,
          included: true,
        });
      }
      
      // Ventilator (Kozijnen): €150 incl. BTW up to 1000 mm sash width, +€10 per 100 mm above
      const ventilatorPriceInclVat = calcKozijnenVentilatorTotalInclVat(config);
      if ((isKozijnenPart1 || isKozijnenMultiParts) && ventilatorPriceInclVat > 0) {
        const ventilatorMainCountDisplay = Array.isArray(config.ventilatorParts) ? config.ventilatorParts.filter(Boolean).length : 0;
        const ventilatorOverheadCountDisplay = Array.isArray(config.ventilatorOverheadParts) ? config.ventilatorOverheadParts.filter(Boolean).length : 0;
        const ventilatorUnderlightCountDisplay = Array.isArray(config.ventilatorUnderlightParts) ? config.ventilatorUnderlightParts.filter(Boolean).length : 0;
        const ventilatorTotalCount = ventilatorMainCountDisplay + ventilatorOverheadCountDisplay + ventilatorUnderlightCountDisplay;
        items.push({
          name: t('options.withVentilator') + (ventilatorTotalCount > 1 ? ` (×${ventilatorTotalCount})` : ''),
          price: ventilatorPriceInclVat,
          included: true,
        });
      }

      const rampDorpelExVat = calcKozijnenRampDorpelExVat(config);
      if (rampDorpelExVat > 0) {
        items.push({
          name: t('options.rampDorpel30mm'),
          price: rampDorpelExVat,
          included: true,
        });
      }

      const glazing36ExVat = calcKozijnenGlazing36mmExVat(config, pageType);
      if (glazing36ExVat > 0) {
        items.push({
          name: t('options.doubleGlass36'),
          price: glazing36ExVat,
          included: true,
        });
      }

      const texturedGlassExVat = calcKozijnenTexturedGlassExVat(config, pageType);
      if (texturedGlassExVat > 0) {
        const textureLabel =
          config.glassTexture === 'chinchilla'
            ? t('options.chinchilla')
            : t('options.satinato');
        items.push({
          name: textureLabel,
          price: texturedGlassExVat,
          included: true,
        });
      }

      const screenExVat = calcElectricScreenPriceExVat(config);
      if (screenExVat > 0) {
        const screenColorData = getColorByRAL(config.screenColor || '7016');
        const screenColorName = screenColorData?.ral
          ? `RAL ${screenColorData.ral.replace(/^0+/, '')}`
          : 'RAL 7016';
        items.push({
          name: `${t('quote.screenElectricallyOperated')} (${screenColorName})`,
          price: screenExVat,
          included: true,
        });
      }

      const rollerExVat = calcElectricRollerShutterPriceExVat(config);
      if (rollerExVat > 0) {
        const shutterColorData = getColorByRAL(config.rollerShutterColor || '7016');
        const shutterColorName = shutterColorData?.ral
          ? `RAL ${shutterColorData.ral.replace(/^0+/, '')}`
          : 'RAL 7016';
        items.push({
          name: `${t('quote.rollerShutterElectricallyOperated')} (${shutterColorName})`,
          price: rollerExVat,
          included: true,
        });
      }
    }
    
    // Ventilator (Voordeur / Achterdeur / Terrasdeur): side panels + overhead only
    if (
      pageType === 'voordeur' ||
      pageType === 'achterdeur' ||
      pageType === 'terrasdeur' ||
      config.doorStyle !== undefined ||
      (config.designType !== undefined && pageType !== 'kozijnen' && pageType !== 'schuifpui')
    ) {
      const doorVentilatorInclVat = calcDoorVentilatorTotalInclVat(config);
      if (doorVentilatorInclVat > 0) {
        const doorVentCount = countDoorVentilators(config);
        items.push({
          name: t('options.withVentilator') + (doorVentCount > 1 ? ` (×${doorVentCount})` : ''),
          price: doorVentilatorInclVat,
          included: true,
        });
      }
      const sidePanelPartsPremium = calcDoorSidePanelPartsPremium(config);
      if (sidePanelPartsPremium > 0) {
        items.push({
          name: t('options.sidePanelParts'),
          price: sidePanelPartsPremium,
          included: true,
        });
      }
    }

    // Wood joint — kozijnen HVL is in calculateConfiguratorPrice; doors show HVL when selected
    if (
      !usesDoorRecreationPricing &&
      config.woodJoint &&
      woodJointOptions &&
      woodJointOptions.length > 0
    ) {
      const woodJoint = woodJointOptions.find(w => w.id === config.woodJoint);
      const jointLabel =
        config.woodJoint === 'joint-45'
          ? t('options.woodJoint45')
          : config.woodJoint === 'joint-90'
            ? t('options.woodJoint90')
            : woodJoint?.name || config.woodJoint;
      const hvlInclVat = calcKozijnenHvlTotalInclVat(config);
      items.push({
        name: `${t('options.woodJoint')}: ${jointLabel}`,
        price: hvlInclVat,
        included: true,
      });
    }

    if (usesDoorRecreationPricing && config.woodJoint) {
      const jointLabel =
        config.woodJoint === 'joint-45'
          ? t('options.woodJoint45')
          : config.woodJoint === 'joint-90'
            ? t('options.woodJoint90')
            : config.woodJoint;
      const doorHvlInclVat = calcVoordeurWoodJointPremium(config);
      if (doorHvlInclVat > 0) {
        items.push({
          name: `${t('options.woodJoint')}: ${jointLabel}`,
          price: doorHvlInclVat,
          included: true,
        });
      } else {
        items.push({
          name: `${t('options.woodJoint')}: ${jointLabel}`,
          price: 0,
          included: true,
        });
      }
    }
    
    // Hardware (included)
    if (config.hardware && Array.isArray(config.hardware) && hardware && hardware.length > 0) {
      config.hardware.forEach(hwId => {
        if (hwId) {
          const hw = hardware.find(h => h.id === hwId);
          const hwPrice = pricing.hardware?.[hwId] || 0;
          items.push({ name: `${t('options.hardware')}: ${hw?.name || hwId}`, price: 0, included: true });
          totalOptions += hwPrice;
        }
      });
    }
    
    // Accessories (included)
    if (config.accessories && Array.isArray(config.accessories) && accessories && accessories.length > 0) {
      config.accessories.forEach(accId => {
        if (accId) {
          const acc = accessories.find(a => a.id === accId);
          const accPrice = pricing.accessories?.[accId] || 0;
          items.push({ name: `${t('options.accessory')}: ${acc?.name || accId}`, price: 0, included: true });
          totalOptions += accPrice;
        }
      });
    }
    
    // Additional Options — kozijnen screen/roller priced in calculateConfiguratorPrice
    if (config.screenElectricallyOperated === true && !isKozijnenPart1 && !isKozijnenMultiParts) {
      const screenColorData = getColorByRAL(config.screenColor || '7016');
      const screenColorName = screenColorData?.ral ? `RAL ${screenColorData.ral.replace(/^0+/, '')}` : 'RAL 7016';
      items.push({ name: `Screen electrically operated (${screenColorName})`, price: 0, included: true });
    }
    
    if (config.rollerShutterElectricallyOperated === true && !isKozijnenPart1 && !isKozijnenMultiParts) {
      const shutterColorData = getColorByRAL(config.rollerShutterColor || '7016');
      const shutterColorName = shutterColorData?.ral ? `RAL ${shutterColorData.ral.replace(/^0+/, '')}` : 'RAL 7016';
      items.push({ name: `Roller shutter electrically operated (${shutterColorName})`, price: 0, included: true });
    }
    
    if (config.hor === true && !isKozijnenPart1 && !isKozijnenMultiParts) {
      const horColorData = getColorByRAL(config.horColor || '7016');
      const horColorName = horColorData?.ral ? `RAL ${horColorData.ral.replace(/^0+/, '')}` : 'RAL 7016';
      items.push({ name: `Hor (${horColorName})`, price: 0, included: true });
    }
    
    if (config.buoySharing === true) {
      const buoyColorData = getColorByRAL(config.buoySharingColor || '7016');
      const buoyColorName = buoyColorData?.ral ? `RAL ${buoyColorData.ral.replace(/^0+/, '')}` : 'RAL 7016';
      const mmText = config.buoySharingMm ? `, ${config.buoySharingMm}mm` : '';
      items.push({ name: `${t('quote.buoySharing')} (${buoyColorName}${mmText})`, price: 0, included: true });
    }

    getFilledAdditionalItems(config.additionalItems).forEach((item) => {
      items.push({
        name: (item.name || '').trim() || t('options.additionalItem'),
        price: 0,
        included: true,
      });
    });
    
    // Only PDF-covered options affect price (via calculateConfiguratorPrice); list options for display only
    return { items, total: 0 };
  };
  
  const { items: optionItems } = calculateOptionPrices();
  
  // Unit price from PDF formulas; option add-ons not in the PDF are excluded
  let totalPrice = hasPricing ? basePrice + additionalItemsTotal : 0;
  const vekaProfileDiscountAmount = hasPricing
    ? calcVekaProfileDiscountAmount(totalPrice, config.profileSystem)
    : 0;
  if (vekaProfileDiscountAmount > 0) {
    totalPrice = applyVekaProfileDiscount(totalPrice, config.profileSystem);
  }
  const drawingPriceAdjustmentExVat = hasPricing
    ? getDrawingPriceAdjustmentExVat(config)
    : 0;
  if (drawingPriceAdjustmentExVat !== 0) {
    totalPrice = applyDrawingPriceAdjustment(totalPrice, config);
  }
  const displayOptionItems =
    vekaProfileDiscountAmount > 0 || drawingPriceAdjustmentExVat !== 0
      ? [
          ...optionItems,
          ...(vekaProfileDiscountAmount > 0
            ? [
                {
                  name: t('options.vekaProfileDiscount'),
                  price: -vekaProfileDiscountAmount,
                  included: false,
                },
              ]
            : []),
          ...(drawingPriceAdjustmentExVat !== 0
            ? [
                {
                  name: t('options.drawingPriceAdjustment'),
                  price: drawingPriceAdjustmentExVat,
                  included: false,
                },
              ]
            : []),
        ]
      : optionItems;
  
  // Apply margin first (silently, only if user is logged in and margin is enabled)
  let priceWithMargin = totalPrice;
  if (isAuthenticated && pricingSettings?.margin_enabled && pricingSettings?.margin_percentage > 0) {
    priceWithMargin = totalPrice * (1 + pricingSettings.margin_percentage / 100);
  }
  
  // Workers (fixing) cost: per window (admin setting). Per-unit amount; display uses totalPrice × quantity.
  let workersFixingAmount = 0;
  if (isAuthenticated && pricingSettings?.workers_fixing_enabled && (pricingSettings?.workers_fixing_amount ?? 0) > 0) {
    workersFixingAmount = pricingSettings.workers_fixing_amount || 0;
  }
  
  // Material cost: per window (admin setting). Per-unit amount; display uses totalPrice × quantity.
  let materialCostAmount = 0;
  if (isAuthenticated && pricingSettings?.material_cost_enabled && (pricingSettings?.material_cost_amount ?? 0) > 0) {
    materialCostAmount = pricingSettings.material_cost_amount || 0;
  }
  
  // Apply surcharge (per frame/part)
  let surchargeAmount = 0;
  if (isAuthenticated && pricingSettings?.surcharge_enabled && pricingSettings?.surcharge_amount > 0) {
    // Get number of parts/frames from config
    const numberOfParts = config.parts || 1;
    surchargeAmount = pricingSettings.surcharge_amount * numberOfParts;
  }
  
  // Discount is set per quote in the Quote dialog, not here
  const finalPrice = applyDeductionMargin(
    priceWithMargin + workersFixingAmount + materialCostAmount + surchargeAmount,
    pricingSettings,
    { isAuthenticated }
  );
  totalPrice = finalPrice;
  
  const formatPrice = (price) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };
  
  const detailConfig = selectedQuote?.config ?? config;
  const detailAreaSqm = (detailConfig.width * detailConfig.height) / 1000000;
  const detailOptionItems = selectedQuote?.pricing?.options ?? displayOptionItems;
  const detailQuantity = selectedQuote?.config?.quantity ?? quantity;
  const detailHasPricing = selectedQuote
    ? calculateConfiguratorPrice(selectedQuote.config, selectedQuote.type).hasPricing
    : hasPricing;
  const detailUnitPrice = selectedQuote?.pricing?.totalPrice ?? totalPrice;
  const detailLineTotal = detailHasPricing ? detailUnitPrice * detailQuantity : 0;
  const detailVatAmount = detailLineTotal * BTW_RATE;
  const detailLineTotalInclVat = detailLineTotal * BTW_MULTIPLIER_INCL;

  const savedConfigurationsTotal = computeDrawingsStoredTotalExVat(projectQuotes);
  const savedConfigurationsVat = savedConfigurationsTotal * BTW_RATE;
  const savedConfigurationsTotalInclVat = computeDrawingsStoredTotalInclVat(projectQuotes);

  const handleSaveSection = () => {
    const trimmed = sectionNameInput.trim();
    if (!trimmed || projectQuotes.length === 0) return;
    saveQuoteSection(trimmed);
    setSectionDialogOpen(false);
    setSectionNameInput('');
    setSelectedKey('current');
  };

  const toggleSectionOpen = (sectionId) => {
    setOpenSectionIds((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const handleSaveCurrent = async () => {
    // Only save the live canvas — never create a duplicate from a selected saved quote
    if (!isCurrentSelection) {
      return;
    }

    const svgData =
      pageType === 'keralit'
        ? await buildKeralitSavedSvgAsync(config)
        : pageType === 'plastisol'
          ? await buildPlastisolSavedSvgAsync(config)
        : pageType === 'dakkapel'
          ? await buildDakkapelSavedSvgAsync(config)
        : captureDrawingSvg(drawingBoardRef, { saved: true }) || '';
    const currentQuantity = parseInt(quantityInput, 10);
    const finalQuantity =
      !isNaN(currentQuantity) && currentQuantity >= 1 ? currentQuantity : config.quantity ?? 1;

    const quoteData = {
      type: resolvedPageType,
      config: {
        ...JSON.parse(JSON.stringify(config)),
        productType: resolvedPageType,
        quantity: finalQuantity,
      },
      pricing: {
        areaSqm: areaSqm.toFixed(2),
        basePrice,
        finalBasePrice: totalPrice,
        options: displayOptionItems.map((item) => ({ ...item })),
        totalOptions: 0,
        totalPrice,
      },
      dimensions: {
        width: config.width,
        height: config.height,
        area: areaSqm.toFixed(2),
      },
      svgData,
    };

    let nextQuotes;
    let quoteId;
    const isUpdatingExisting = Boolean(editingQuoteId);
    if (isUpdatingExisting) {
      updateQuote(editingQuoteId, quoteData);
      quoteId = editingQuoteId;
      nextQuotes = savedQuotes.map((quote) =>
        quote.id === editingQuoteId ? { ...quote, ...quoteData } : quote
      );
    } else {
      quoteId = saveQuote(quoteData);
      nextQuotes = [
        { id: quoteId, ...JSON.parse(JSON.stringify(quoteData)), savedAt: new Date().toISOString() },
        ...savedQuotes,
      ];
    }

    if (isAuthenticated && canOverwriteBackendQuote(activeBackendQuote)) {
      await syncEditedQuoteToBackend(nextQuotes);
    }

    if (isUpdatingExisting) {
      // End edit session and return to the live "current" board (not a previously browsed saved card).
      const resume = location.state?.resumeAfterEdit;
      const resumePath = resume?.pathname || location.pathname;

      if (resumePath !== location.pathname) {
        setEditingQuoteId(null);
        setSelectedKey('current');
        navigate(resumePath, {
          replace: true,
          state: buildConfiguratorNavigationState(location.state, {}, user?.id),
        });
      } else {
        clearEditSession({ selectCurrent: true });
      }
    }

    startTransition(() => {
      setSaveNotice(isUpdatingExisting ? 'updated' : 'created');
      setSavedQuoteId(quoteId);
      // After save or update: live board + right preview both show a fresh current drawing.
      setSelectedKey('current');
      onSaved?.(null, { resetToDefault: true });
    });
    setTimeout(() => {
      setSavedQuoteId(null);
      setSaveNotice(null);
    }, 3000);

    return quoteData.config;
  };

  if (saveCurrentRef) {
    saveCurrentRef.current = isCurrentSelection ? handleSaveCurrent : null;
  }

  const syncEditedQuoteToBackend = async (drawings) => {
    try {
      const clientDetails = { ...(activeBackendQuote.clientDetails || {}) };
      const totalAmount = computeStoredQuoteTotalInclVat(drawings, clientDetails, quoteSections);
      const contactIdForApi = resolveQuoteContactId(clientDetails);

      const quotePayload = {
        quote_data: withQuoteCreator(
          {
            ...serializeQuoteDrawingsPayload(drawings, quoteSections),
            clientDetails,
            exportOptions: {
              includePricingInExport,
              includeTemplateInExport,
              includeNijBegunInExport,
              includeTotalPageInExport: includeTemplateInExport || includeTotalPageInExport,
            },
          },
          user,
          preserveCreatorFromBackendQuote(activeBackendQuote),
        ),
        status: activeBackendQuote.status || 'draft',
        total_amount: totalAmount,
        valid_until:
          activeBackendQuote?.valid_until ||
          new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        ...(contactIdForApi ? { contact_id: contactIdForApi } : {}),
      };

      await axios.put(`${API_URL}/api/quotes/${activeBackendQuote.id}`, quotePayload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      patchActiveBackendQuote?.({
        clientDetails,
        clientContactOverride: activeBackendQuote.clientContactOverride,
      });
    } catch (error) {
      console.error('Error syncing edited quote to backend:', error);
    }
  };

  const activeDetailIsKeralit = isCurrentSelection
    ? pageType === 'keralit'
    : selectedQuote?.type === 'keralit';
  const activeDetailIsPlastisol = isCurrentSelection
    ? pageType === 'plastisol'
    : selectedQuote?.type === 'plastisol';
  const activeCladdingProfileLogo = activeDetailIsPlastisol
    ? getPlastisolProfileLogo(detailConfig?.profileSystem)
    : activeDetailIsKeralit
      ? getKeralitProfileLogo(detailConfig?.profileSystem)
      : null;

  const activePreviewDataUrl = isCurrentSelection
    ? activeCladdingProfileLogo
      ? null
      : svgHtmlToDataUrl(currentThumbnail, { saved: false })
    : selectedQuote?.type === 'keralit' || selectedQuote?.type === 'plastisol'
      ? null
      : selectedQuote?.svgData
        ? svgHtmlToDataUrl(selectedQuote.svgData, { saved: true })
        : null;

  const renderListItem = ({
    isSelected,
    isActive = false,
    onClick,
    onDoubleClick,
    thumbnail,
    title,
    subtitle,
    quantity: itemQuantity,
    saved = false,
    keralitProfileSystem,
    plastisolProfileSystem,
  }) => (
    <button
      type="button"
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      title={onDoubleClick ? t('summary.doubleClickToEdit') : undefined}
      className={`relative w-full text-left flex flex-col gap-1.5 p-2 rounded-lg border transition-colors ${
        isActive
          ? 'border-orange-400 bg-orange-50 shadow-sm ring-1 ring-orange-200'
          : isSelected
            ? 'border-orange-200 bg-orange-50/50'
            : 'border-gray-200 bg-white hover:border-orange-200 hover:bg-gray-50'
      }`}
    >
      {isActive ? (
        <span className="absolute top-2 right-2 z-10 text-[9px] font-bold uppercase tracking-wide text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded">
          {t('summary.active')}
        </span>
      ) : null}
      <SummaryConfigThumbnail
        svgData={thumbnail}
        saved={saved}
        keralitProfileSystem={keralitProfileSystem}
        plastisolProfileSystem={plastisolProfileSystem}
        className="w-full h-[80px]"
      />
      <div className="min-w-0 w-full pr-6">
        <div className="text-[11px] font-semibold text-gray-900 leading-tight line-clamp-2">{title}</div>
        <div className="text-[10px] text-gray-500 truncate mt-0.5">{subtitle}</div>
        <div className="text-[10px] font-medium text-gray-400 mt-0.5">×{itemQuantity}</div>
      </div>
      {saved ? (
        <span
          className="absolute bottom-2 right-2 flex h-5 w-5 items-center justify-center rounded-full bg-green-600 text-white shadow-sm"
          aria-hidden="true"
        >
          <Check className="h-3 w-3" strokeWidth={3} />
        </span>
      ) : null}
    </button>
  );

  // While editing a saved drawing, the live "current" slot IS that drawing —
  // hide the original saved entry so the list doesn't show two identical cards.
  const isEditingSavedDrawing = Boolean(editingQuoteId && isCurrentSelection);
  const editingQuoteIndex = isEditingSavedDrawing
    ? projectQuotes.findIndex((quote) => quote.id === editingQuoteId)
    : -1;
  const editingLetterLabel =
    editingQuoteIndex >= 0 ? getLetterLabel(editingQuoteIndex) : null;

  // Configuraties list shows only saved drawings. The live board is always
  // represented in the right detail panel ("Huidige configuratie"), not as a list card.
  // ACTIEF only while that saved drawing is open for edit.
  const activeEditingId = editingQuoteId || editQuoteIdFromRoute || null;
  const configurationListItems = projectQuotes.map((quote) => {
    const index = projectQuotes.findIndex((q) => q.id === quote.id);
    const qty = quote.config?.quantity ?? 1;
    const label = getLetterLabel(index);
    const isBeingEdited = activeEditingId === quote.id;
    return {
      key: quote.id,
      isSelected: !isBeingEdited && selectedKey === quote.id,
      isActive: isBeingEdited,
      onClick: () => rememberSelectionBeforeBrowse(quote.id),
      onDoubleClick: (event) => {
        event.preventDefault();
        startEditSavedDrawing(quote);
      },
      thumbnail: quote.svgData,
      title: `${qty}× <${label}> ${formatDrawingSizeLabel(quote.type, quote.config)}`,
      subtitle: getListItemSubtitle(quote.type, quote.config, t),
      quantity: qty,
      saved: true,
      keralitProfileSystem: quote.type === 'keralit' ? quote.config?.profileSystem : undefined,
      plastisolProfileSystem: quote.type === 'plastisol' ? quote.config?.profileSystem : undefined,
    };
  });

  return (
    <Card className="h-full flex flex-col min-w-0">
      <CardHeader className="pb-2 border-b px-3 pt-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base font-bold leading-tight">{t('summary.title')}</CardTitle>
          <div className="flex flex-row flex-wrap items-center justify-end gap-x-3 gap-y-1 shrink-0">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox
                id="include-pricing-in-export"
                checked={includePricingInExport}
                onCheckedChange={(checked) => setIncludePricingInExport(checked === true)}
                className="h-3.5 w-3.5"
              />
              <span className="text-[10px] font-medium text-gray-600 leading-tight">
                {t('summary.includePricingInExport')}
              </span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox
                id="include-template-in-export"
                checked={includeTemplateInExport}
                onCheckedChange={(checked) => setIncludeTemplateInExport(checked === true)}
                className="h-3.5 w-3.5"
              />
              <span className="text-[10px] font-medium text-gray-600 leading-tight">
                {t('summary.includeTemplateInExport')}
              </span>
            </label>
            <label
              className={`flex items-center gap-1.5 ${includeTemplateInExport ? 'cursor-default opacity-70' : 'cursor-pointer'}`}
            >
              <Checkbox
                id="include-total-page-in-export"
                checked={includeTemplateInExport || includeTotalPageInExport}
                disabled={includeTemplateInExport}
                onCheckedChange={(checked) => {
                  if (!includeTemplateInExport) {
                    setIncludeTotalPageInExport(checked === true);
                  }
                }}
                className="h-3.5 w-3.5"
              />
              <span className="text-[10px] font-medium text-gray-600 leading-tight">
                {t('summary.includeTotalPageInExport')}
              </span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <Checkbox
                id="include-nij-begun-in-export"
                checked={includeNijBegunInExport}
                onCheckedChange={(checked) => setIncludeNijBegunInExport(checked === true)}
                className="h-3.5 w-3.5"
              />
              <span className="text-[10px] font-medium text-gray-600 leading-tight">
                {t('summary.includeNijBegunInExport')}
              </span>
            </label>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 p-0 flex flex-col">
        <div className={`flex flex-1 min-h-0 ${compact ? 'flex-col' : 'flex-row'}`}>
          {/* Configuration list */}
          <div
            className={`flex-shrink-0 flex flex-col min-h-0 bg-slate-50 ${
              compact ? 'w-full max-h-[36vh] border-b border-gray-200' : 'w-[220px] border-r border-gray-200'
            }`}
          >
            <div className="px-2 py-2 border-b border-gray-200 shrink-0">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                  {t('summary.configurations')}
                </p>
                <span className="inline-flex items-center rounded-full bg-slate-200/80 px-2 py-0.5 text-[10px] font-semibold text-slate-700 tabular-nums">
                  {t('summary.totalDrawings', {
                    count: countQuoteDrawings(quoteSections, projectQuotes),
                  })}
                </span>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
              {quoteSections.length > 0 && (
                <div className="border-b border-gray-200 px-2 py-2 space-y-1.5 bg-white">
                  <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide px-0.5">
                    {t('summary.savedSections')}
                  </p>
                  {quoteSections.map((section, sectionIndex) => {
                    const sectionTotalIncl = computeDrawingsStoredTotalInclVat(section.drawings || []);
                    const isOpen = openSectionIds.has(section.id);
                    return (
                      <Collapsible key={section.id} open={isOpen} onOpenChange={() => toggleSectionOpen(section.id)}>
                        <div className="rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
                          <CollapsibleTrigger asChild>
                            <button
                              type="button"
                              className="w-full flex items-center justify-between gap-2 px-2 py-2 text-left hover:bg-slate-100 transition-colors"
                            >
                              <div className="min-w-0">
                                <div className="text-[11px] font-semibold text-gray-900 truncate">
                                  {section.name}
                                </div>
                                <div className="text-[10px] text-gray-500">
                                  {sumDrawingQuantities(section.drawings || [])} {t('summary.drawingsCount')} · {formatPrice(sectionTotalIncl)}
                                </div>
                              </div>
                              <ChevronDown className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                            </button>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="px-2 pb-2 space-y-1 border-t border-slate-200 pt-1.5">
                              <div className="max-h-56 overflow-y-auto space-y-1 pr-0.5">
                                {(section.drawings || []).map((drawing, drawingIndex) => (
                                  <div
                                    key={drawing.id || `${section.id}-${drawingIndex}`}
                                    className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5"
                                  >
                                    <SummaryConfigThumbnail
                                      svgData={drawing.svgData}
                                      saved
                                      keralitProfileSystem={drawing.type === 'keralit' ? drawing.config?.profileSystem : undefined}
                                      plastisolProfileSystem={drawing.type === 'plastisol' ? drawing.config?.profileSystem : undefined}
                                      className="w-10 h-10 shrink-0"
                                    />
                                    <div className="min-w-0 flex-1">
                                      <div className="text-[10px] font-medium text-gray-800 truncate">
                                        &lt;{getLetterLabel(drawingIndex)}&gt;{' '}
                                        {formatDrawingSizeLabel(drawing.type, drawing.config)}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {projectQuotes.length > 0 ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="w-full h-7 text-[10px] text-blue-700 hover:text-blue-800 hover:bg-blue-50"
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        t('summary.addToSectionConfirm', {
                                          name: section.name,
                                          count: projectQuotes.length,
                                        })
                                      )
                                    ) {
                                      addCurrentDrawingsToQuoteSection(section.id);
                                    }
                                  }}
                                >
                                  <FolderInput className="h-3 w-3 mr-1" />
                                  {t('summary.addToSection')}
                                </Button>
                              ) : null}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="w-full h-7 text-[10px] text-slate-700 hover:text-slate-900 hover:bg-slate-100"
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      t('summary.ungroupSectionConfirm', { name: section.name })
                                    )
                                  ) {
                                    ungroupQuoteSection(section.id);
                                  }
                                }}
                              >
                                <FolderMinus className="h-3 w-3 mr-1" />
                                {t('summary.ungroupSection')}
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="w-full h-7 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => {
                                  if (window.confirm(t('summary.removeSectionConfirm', { name: section.name }))) {
                                    removeQuoteSection(section.id);
                                  }
                                }}
                              >
                                <Trash2 className="h-3 w-3 mr-1" />
                                {t('summary.removeSection')}
                              </Button>
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    );
                  })}
                </div>
              )}
              <div className="px-2 pt-2">
                <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                  {quoteSections.length > 0 ? t('summary.currentSection') : t('summary.configurations')}
                </p>
              </div>
              <div className="p-2 space-y-1.5">
                {configurationListItems.length === 0 ? (
                  <p className="px-1 py-3 text-[11px] text-gray-500 leading-snug">
                    {t('summary.noSavedDrawingsYet')}
                  </p>
                ) : (
                  configurationListItems.map((item) => (
                    <React.Fragment key={item.key}>{renderListItem(item)}</React.Fragment>
                  ))
                )}
              </div>
            </div>
            {projectQuotes.length > 0 && (
              <div className="border-t border-gray-200 px-2 py-2 bg-white space-y-2 shrink-0">
                <div className="space-y-1">
                  <div className="text-[10px] font-semibold text-gray-600 uppercase tracking-wide">
                    {quoteSections.length > 0 ? t('summary.sectionTotal') : t('summary.projectTotal')}
                  </div>
                  <div className="flex justify-between items-baseline gap-2 text-[11px]">
                    <span className="text-gray-500">{t('common.subtotal')}</span>
                    <span className="font-medium text-gray-800">{formatPrice(savedConfigurationsTotal)}</span>
                  </div>
                  <div className="flex justify-between items-baseline gap-2 text-[11px]">
                    <span className="text-gray-500">{t('quote.vat21')}</span>
                    <span className="font-medium text-gray-800">{formatPrice(savedConfigurationsVat)}</span>
                  </div>
                  <div className="flex justify-between items-baseline gap-2 text-xs pt-0.5 border-t border-gray-100">
                    <span className="font-semibold text-gray-700">{t('common.total')}</span>
                    <span className="font-bold text-orange-600">{formatPrice(savedConfigurationsTotalInclVat)}</span>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="default"
                  onClick={() => {
                    setSectionNameInput('');
                    setSectionDialogOpen(true);
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center gap-1.5 h-8 text-[11px]"
                >
                  <FolderPlus className="w-3.5 h-3.5" />
                  {t('summary.saveAsSection')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (window.confirm(t('savedQuotes.removeAllDrawingsConfirm'))) {
                      clearAllQuotes();
                      setSelectedKey('current');
                    }
                  }}
                  className="w-full text-red-600 border-red-200 hover:bg-red-50 flex items-center justify-center gap-1.5 h-8 text-[11px]"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {t('savedQuotes.removeAllDrawings')}
                </Button>
              </div>
            )}
          </div>

          {/* Detail panel */}
          <div className={`flex-1 min-w-0 overflow-y-auto p-2.5 ${compact ? 'w-full' : 'w-[280px] shrink-0'}`}>
            <div className="mb-3">
              <h3 className="text-sm font-bold text-gray-900">
                {isEditingSavedDrawing && editingLetterLabel
                  ? `${t('summary.configuration')} <${editingLetterLabel}>`
                  : isCurrentSelection
                    ? t('summary.currentConfiguration')
                    : `${t('summary.configuration')} <${getLetterLabel(projectQuotes.findIndex((q) => q.id === selectedKey))}>`}
              </h3>
              {isCurrentSelection ? (
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {getConfiguratorTypeLabel(resolvedPageType, t)}
                </p>
              ) : selectedQuote ? (
                <p className="text-[11px] text-gray-500 mt-0.5">
                  {getListItemSubtitle(selectedQuote.type, selectedQuote.config, t)}
                </p>
              ) : null}
            </div>

            {(activeCladdingProfileLogo || activePreviewDataUrl) && (
              <div
                className={`mb-3 flex justify-center items-center overflow-hidden min-h-[140px] max-h-[200px] w-full ${
                  isCurrentSelection
                    ? 'p-1'
                    : 'p-2 bg-white border border-gray-200 rounded-lg'
                }`}
              >
                <img
                  src={activeCladdingProfileLogo || activePreviewDataUrl}
                  alt=""
                  className="max-h-[180px] max-w-full w-full h-auto object-contain object-center"
                  draggable={false}
                />
              </div>
            )}

            <div className="space-y-3">
              <div>
                <h4 className="font-semibold text-xs text-gray-700 mb-1.5">{t('summary.dimensions')}</h4>
                <div className="text-xs text-gray-600 space-y-0.5">
                  <div>{t('summary.width')}: {detailConfig.width} mm</div>
                  <div>{t('summary.height')}: {detailConfig.height} mm</div>
                  <div>{t('summary.area')}: {detailAreaSqm.toFixed(2)} m²</div>
                  {isCurrentSelection && (config.overhead === 'overhead' || (Array.isArray(config.overheadPerPart) && config.overheadPerPart.some(Boolean))) && (
                    <div>
                      {t('options.overhead')}: {config.overheadParts || 1} {t('summary.part')}
                      {config.overheadParts > 1 ? t('summary.parts') : ''} — {config.overheadHeight} mm
                    </div>
                  )}
                  {isCurrentSelection && config.underlight === 'underlight' && (
                    <div>
                      {t('options.underlight')}: {config.underlightParts || 1} {t('summary.part')}
                      {config.underlightParts > 1 ? t('summary.parts') : ''} — {config.underlightHeight} mm
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-200" />

              <div>
                <h4 className="font-semibold text-xs text-gray-700 mb-1.5">{t('summary.options')}</h4>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {detailOptionItems.length > 0 ? (
                    detailOptionItems.map((item, index) => (
                      <div key={index} className="flex justify-between items-start gap-2 text-xs">
                        <span className="text-gray-600 leading-snug">{item.name}</span>
                        <span
                          className={`font-medium flex-shrink-0 tabular-nums ${
                            item.included || !(Number(item.price) > 0 || Number(item.price) < 0)
                              ? 'text-gray-400'
                              : Number(item.price) < 0
                                ? 'text-orange-600'
                                : 'text-gray-700'
                          }`}
                        >
                          {item.included || !(Number(item.price) > 0 || Number(item.price) < 0)
                            ? t('summary.included')
                            : `${Number(item.price) > 0 ? '+' : ''}${formatPrice(Number(item.price) * BTW_MULTIPLIER_INCL)}`}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="text-xs text-gray-400">{t('summary.noOptions')}</p>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-200" />

              <div className="flex justify-between items-center">
                <label htmlFor="quantity" className="text-xs font-semibold text-gray-700">
                  {t('summary.quantity')}
                </label>
                {isCurrentSelection ? (
                  <input
                    id="quantity"
                    type="number"
                    min="1"
                    value={quantityInput}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      setQuantityInput(inputValue);
                      const parsedValue = parseInt(inputValue, 10);
                      if (!isNaN(parsedValue) && parsedValue >= 1) {
                        onQuantityInputChange?.(parsedValue);
                        onConfigChange?.('quantity', parsedValue);
                      }
                    }}
                    onBlur={(e) => {
                      const parsedValue = parseInt(e.target.value.trim(), 10);
                      if (isNaN(parsedValue) || parsedValue < 1) {
                        setQuantityInput('1');
                        onConfigChange?.('quantity', 1);
                      } else {
                        setQuantityInput(String(parsedValue));
                      }
                    }}
                    className="w-16 px-2 py-1 border border-gray-300 rounded text-xs text-right"
                  />
                ) : (
                  <span className="text-xs font-medium text-gray-800">{detailQuantity}</span>
                )}
              </div>

              <div className="border-t border-gray-200" />

              <div>
                {!detailHasPricing && (
                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
                    {t('summary.pricingUnavailable')}
                  </p>
                )}
                <div className="space-y-1.5 mb-3">
                  <div className="text-sm font-bold text-gray-800">{t('summary.totalPrice')}</div>
                  {detailHasPricing ? (
                    <>
                      <div className="flex justify-between items-baseline gap-2 text-xs">
                        <span className="text-gray-500">{t('common.subtotal')}</span>
                        <span className="font-medium text-gray-800">{formatPrice(detailLineTotal)}</span>
                      </div>
                      <div className="flex justify-between items-baseline gap-2 text-xs">
                        <span className="text-gray-500">{t('quote.vat21')}</span>
                        <span className="font-medium text-gray-800">{formatPrice(detailVatAmount)}</span>
                      </div>
                      <div className="flex justify-between items-baseline gap-2 text-sm pt-1 border-t border-gray-100">
                        <span className="font-semibold text-gray-700">{t('common.total')}</span>
                        <span className="font-bold text-orange-600">{formatPrice(detailLineTotalInclVat)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-gray-400">—</div>
                  )}
                </div>

                {isCurrentSelection ? (
                  <Button
                    onClick={handleSaveCurrent}
                    className="w-full bg-green-600 hover:bg-green-700 text-white flex items-center justify-center gap-2 h-9 text-xs"
                    variant="default"
                  >
                    {savedQuoteId ? (
                      <>
                        <Check className="w-4 h-4" />
                        {saveNotice === 'updated'
                          ? t('summary.drawingUpdated')
                          : t('summary.quoteSaved')}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        {editingQuoteId && isCurrentSelection
                          ? t('summary.updateDrawing')
                          : t('summary.saveQuote')}
                      </>
                    )}
                  </Button>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={selectCurrentConfiguration}
                      variant="outline"
                      className="w-full flex items-center justify-center gap-2 h-9 text-xs"
                    >
                      {t('summary.backToCurrent')}
                    </Button>
                    <Button
                      onClick={() => startEditSavedDrawing(selectedQuote)}
                      variant="outline"
                      className="w-full flex items-center justify-center gap-2 h-9 text-xs border-orange-200 text-orange-700 hover:bg-orange-50"
                    >
                      <Pencil className="w-4 h-4" />
                      {t('summary.editDrawing') || 'Edit drawing'}
                    </Button>
                    <Button
                      onClick={() => {
                        if (window.confirm(t('savedQuotes.removeThisConfirm'))) {
                          removeQuote(selectedQuote.id);
                          setSelectedKey('current');
                        }
                      }}
                      variant="outline"
                      className="w-full text-red-600 border-red-200 hover:bg-red-50 flex items-center justify-center gap-2 h-9 text-xs"
                    >
                      <Trash2 className="w-4 h-4" />
                      {t('savedQuotes.removeThisDrawing')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>

      <Dialog open={sectionDialogOpen} onOpenChange={setSectionDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('summary.saveAsSection')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="section-name">{t('summary.sectionName')}</Label>
            <Input
              id="section-name"
              value={sectionNameInput}
              onChange={(e) => setSectionNameInput(e.target.value)}
              placeholder={t('summary.sectionNamePlaceholder')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveSection();
              }}
            />
            <p className="text-xs text-gray-500">{t('summary.saveSectionHint')}</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSectionDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleSaveSection}
              disabled={!sectionNameInput.trim() || projectQuotes.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {t('summary.saveSection')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default Summary;

